/* LE JOURNAL EST FAIT DE NOTES (prototype de Codex, 2026-10-01).
 *
 * Une note : { id, date, title, text, author, tags: [clé], decision }.
 * `decision` vaut null, ou { owner, date, due, status } quand la note se SUIT : une
 * décision n'est plus une liste à part, c'est une note qui a un responsable,
 * une échéance et un statut, visible dans les deux vues (Journal, Décisions).
 * Les ÉTIQUETTES appartiennent au CLIENT (2026-10-09) : une liste
 * { id, label } qu'on enrichit depuis la note et qu'on renomme ou supprime à un
 * seul endroit. Une note pointe vers l'id, si bien qu'un renommage vaut pour
 * toutes. Les cinq de départ (`NOTE_TAGS`) gardent un libellé vide tant qu'on
 * ne les renomme pas : l'écran les traduit, une étiquette créée par le client
 * reste dans sa langue.
 *
 * Le document reste celui de `governance_journals` (route
 * /api/governance/journal, périmètre `governance`), sous la forme
 * { version: 2, notes, tags }. Les formats précédents se reprennent SANS PERTE à la
 * lecture : une observation devient une note, une décision rejoint la note
 * d'où elle venait (ou devient la sienne), son statut se traduit. Le numéro
 * « D{n} » ne survit pas, la décision se désignant désormais par son titre.
 */

export const NOTE_TAGS = ['liquidity', 'sales', 'personnel', 'investments', 'result'];
export const TAG_MAX = 40;
/* « À valider » ouvre la liste (demande d'Olivier, 2026-10-03) : une décision
   qu'on note avant qu'elle soit arrêtée. */
export const DECISION_STATUSES = ['to_validate', 'todo', 'doing', 'done'];

const LEGACY_STATUS = { ouvert: 'todo', en_cours: 'doing', fait: 'done' };

function uid() {
  return Math.random().toString(36).slice(2, 10);
}
export function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/* Le titre est la première ligne, le reste fait le texte. */
function split(texte) {
  const s = String(texte || '').trim();
  const nl = s.indexOf('\n');
  if (nl < 0) return { title: s, text: '' };
  return { title: s.slice(0, nl).trim(), text: s.slice(nl + 1).trim() };
}

export function normDecision(d) {
  if (!d || typeof d !== 'object') return null;
  return {
    owner: d.owner || '',
    // La date de la DÉCISION (passée ou à venir) et l'échéance sont
    // facultatives toutes deux (2026-10-03).
    date: d.date || '',
    due: d.due || '',
    status: DECISION_STATUSES.includes(d.status) ? d.status : 'todo',
  };
}

/* Une décision DATÉE est prise (2026-10-09) : poser sa date relève une
   échéance antérieure (une échéance vide le reste) et la sort de « À
   valider » ; une échéance ne précède jamais la décision. */
export function withDecisionDate(d, date) {
  const next = { ...d, date };
  if (date && d.due && d.due < date) next.due = date;
  if (date && d.status === 'to_validate') next.status = 'todo';
  return next;
}
export function withDue(d, due) {
  return { ...d, due: due && d.date && due < d.date ? d.date : due };
}

export function normNote(n) {
  return {
    id: n.id || uid(),
    date: n.date || todayISO(),
    title: n.title || '',
    text: n.text || '',
    author: n.author || '',
    tags: Array.isArray(n.tags) ? [...new Set(n.tags.filter((t) => typeof t === 'string' && t))] : [],
    decision: normDecision(n.decision),
  };
}

/* ── Reprise des formats d'avant le 2026-10-01 ───────────────────────── */
function legacyLists(o) {
  const observations = [];
  const decisions = [];
  const obs = (x) => observations.push({ id: x.id || uid(), date: x.date || todayISO(), texte: x.texte || x.body || '' });
  const dec = (x, extra = {}) => decisions.push({
    id: x.id || uid(),
    texte: x.texte || x.label || x.text || '',
    responsable: x.responsable || '',
    echeance: x.echeance || x.date || '',
    statut: x.statut || 'ouvert',
    observationId: x.observationId || null,
    ...extra,
  });
  if (Array.isArray(o.observations) || Array.isArray(o.decisions)) {
    (o.observations || []).forEach(obs);
    (o.decisions || []).forEach((d) => dec(d));
  } else if (Array.isArray(o.notes)) {
    // Modèle imbriqué { notes: [{ date, texte, todos }] }.
    o.notes.filter(Boolean).forEach((n) => {
      if ((n.texte || n.body || '').trim()) obs(n);
      (Array.isArray(n.todos) ? n.todos : []).filter((t) => t && (t.label || t.text))
        .forEach((t) => dec(t, { echeance: n.date }));
    });
  } else {
    // Formats les plus anciens { messages, todo, archive, journal }.
    if (Array.isArray(o.journal)) {
      o.journal.filter((e) => e && (e.body || e.texte || e.date))
        .forEach((e) => obs({ id: e.id, date: e.date, texte: e.body || e.texte || '' }));
    }
    if (typeof o.messages === 'string' && o.messages.trim()) obs({ texte: o.messages.trim() });
    if (Array.isArray(o.todo)) o.todo.filter((t) => t && t.text).forEach((t) => dec({ id: t.id, texte: t.text }));
    if (Array.isArray(o.archive)) o.archive.filter((t) => t && t.text).forEach((t) => dec({ id: t.id, texte: t.text, statut: 'fait' }));
  }
  return { observations, decisions };
}

function fromLegacy(o) {
  const { observations, decisions } = legacyLists(o);
  const notes = observations.filter((x) => x.texte.trim()).map((x) => ({
    id: x.id, date: x.date, ...split(x.texte), author: '', tags: [], decision: null,
  }));
  const byId = new Map(notes.map((n) => [n.id, n]));
  decisions.filter((d) => d.texte.trim()).forEach((d) => {
    const decision = {
      owner: d.responsable,
      due: d.echeance,
      status: LEGACY_STATUS[d.statut] || 'todo',
    };
    const source = d.observationId ? byId.get(d.observationId) : null;
    if (source && !source.decision) {
      source.decision = decision;
      return;
    }
    notes.push({
      id: d.id, date: (source && source.date) || d.echeance || todayISO(),
      ...split(d.texte), author: '', tags: [], decision,
    });
  });
  return notes.map(normNote);
}

/* ── Étiquettes du client ─────────────────────────────────────────────── */
const defaultTags = () => NOTE_TAGS.map((id) => ({ id, label: '' }));

/** Le document lu → la liste des étiquettes du client (les cinq de départ
    tant qu'il n'en a pas). */
export function parseTags(data) {
  if (!data || typeof data !== 'object' || !Array.isArray(data.tags)) return defaultTags();
  return data.tags.filter((t) => t && t.id)
    .map((t) => ({ id: String(t.id), label: String(t.label || '').slice(0, TAG_MAX) }));
}

/** Pour comparer deux libellés : sans accents, sans casse, espaces réduits. */
export function tagKey(label) {
  return String(label || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/\s+/g, ' ').trim();
}

export function newTag(label) {
  return { id: `t_${uid()}`, label: String(label || '').trim().slice(0, TAG_MAX) };
}

/** Supprimer une étiquette la retire aussi des notes qui la portaient. */
export function removeTag(notes, tags, id) {
  return {
    notes: notes.map((n) => (n.tags.includes(id) ? { ...n, tags: n.tags.filter((x) => x !== id) } : n)),
    tags: tags.filter((t) => t.id !== id),
  };
}

/** Le document lu (objet JSON, ou null) → la liste des notes. Une note ne
    garde que les étiquettes que le client connaît. */
export function parseJournal(data) {
  if (!data || typeof data !== 'object') return [];
  if (data.version === 2 && Array.isArray(data.notes)) {
    const known = new Set(parseTags(data).map((t) => t.id));
    return data.notes.map(normNote).map((n) => ({ ...n, tags: n.tags.filter((t) => known.has(t)) }));
  }
  return fromLegacy(data);
}

/** Les notes et les étiquettes → le document à enregistrer. */
export function serializeJournal(notes, tags = defaultTags()) {
  return { version: 2, notes: notes.map(normNote), tags: tags.map((t) => ({ id: t.id, label: t.label || '' })) };
}

/* Les notes du plus récent au plus ancien ; à date égale, l'ordre de saisie
   s'inverse aussi (la dernière écrite en tête). */
export function sortNotes(notes) {
  return notes
    .map((n, i) => ({ n, i }))
    .sort((a, b) => (a.n.date === b.n.date ? b.i - a.i : (a.n.date < b.n.date ? 1 : -1)))
    .map((x) => x.n);
}

/* Les décisions par échéance, la plus proche d'abord ; sans échéance, en fin. */
export function sortDecisions(notes) {
  return notes.filter((n) => n.decision).slice().sort((a, b) => {
    const da = a.decision.due || '9999-12-31';
    const db = b.decision.due || '9999-12-31';
    return da === db ? 0 : (da < db ? -1 : 1);
  });
}
